import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellRing } from 'lucide-react';
import { Venue, UserProfile, toggleAlert } from '../services/api';
import { Analytics } from '../services/analytics';

interface VenueAlertButtonProps {
  venue: Venue;
  userProfile: UserProfile | null;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

const BUTTON_BASE = `w-full mt-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium rounded-full
  transition-all duration-300 flex items-center justify-center gap-1 select-none active:scale-95`;

const VenueAlertButton: React.FC<VenueAlertButtonProps> = ({ venue, userProfile, setUserProfile }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Alerts only fire for venues we scrape, so don't offer one anywhere else.
  if (!venue.tracks_availability) {
    return null;
  }

  if (!userProfile?.is_pro) {
    return (
      <Link
        to="/availability"
        className={`${BUTTON_BASE} bg-ink-50 text-ink-600 hover:bg-ink-100`}
        title="Get an email when new tables open here"
      >
        <Bell className="w-3 h-3" />
        Alert me
        <span className="ml-0.5 rounded-full bg-clay px-1.5 py-0.5 text-[10px] leading-none text-white">
          PRO
        </span>
      </Link>
    );
  }

  const isOn = (userProfile.alert_venue_ids ?? []).includes(venue.id);

  const handleClick = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await toggleAlert(venue.id);
      setUserProfile(prev => (prev ? { ...prev, alert_venue_ids: updated.alert_venue_ids } : prev));
      Analytics.logEvent('Venue Alert', isOn ? 'Off' : 'On', { venue: venue.name });
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Couldn't update the alert. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={saving}
        aria-pressed={isOn}
        title={isOn ? "We'll email you when new tables open here" : 'Get an email when new tables open here'}
        className={`${BUTTON_BASE} disabled:opacity-60 ${
          isOn
            ? 'bg-green-100 text-green-800 hover:bg-green-200'
            : 'bg-ink-50 text-ink-700 hover:bg-ink-100'
        }`}
      >
        {isOn ? <BellRing className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
        {isOn ? 'Alerts on' : 'Alert me'}
      </button>
      {error && <p className="mt-1 text-[11px] text-red-600 text-center">{error}</p>}
    </>
  );
};

export default VenueAlertButton;
